// GeoWissenApp — public, crawlable "Geo-Wissen" knowledge hub (glossary + FAQ).
// Own nested path-based router (real URLs for /geo-wissen/glossar/<slug>,
// needed for prerendering and crawlers without JS). PublicLayout.jsx has its
// own top-level URL routing (PAGE_ROUTES) for all public pages, but no
// nested sub-routes — Geo-Wissen is the only page with real sub-paths below
// its own root, which is why it still needs this separate internal router.
// GeoWissenApp mounts as PublicLayout.jsx's "geowissen" activePage branch,
// sharing its already-rendered header/footer. See CLAUDE.md decisions log /
// geo_audit_georev_ai.md for background.
import React, { createContext, useContext, useEffect, useState } from "react";
import JsonLd from "./JsonLd";
import { useLanguage } from "../LanguageContext";
import { WISSEN_TERMS, CATEGORY_BADGE_TONE, getTermBySlug } from "./wissenData";

const SITE_URL = "https://georev.ai";
const WISSEN_MARKER = "/geo-wissen";

// Picks the active-language value of a { de, en, fr, it } field, falling
// back to German — same pattern as PublicLayout.jsx's PLAN_FEATURES
// (`plan.features[lang] || plan.features.de`).
function L(field, lang) {
  return (field && (field[lang] || field.de)) ?? field;
}

function categoryLabel(cat, t) {
  if (cat === "grundlagen") return t("geoWissen.catGrundlagen");
  if (cat === "technisch") return t("geoWissen.catTechnisch");
  return t("geoWissen.catProdukt");
}

// ------------------------------------------------------------- Routing -----
function getBase(pathname) {
  const idx = pathname.indexOf(WISSEN_MARKER);
  return idx === -1 ? WISSEN_MARKER : pathname.slice(0, idx + WISSEN_MARKER.length);
}

function parseRoute(pathname) {
  const base = getBase(pathname);
  const sub = pathname.slice(base.length).replace(/\/$/, "");
  if (!sub) return { page: "hub", base };
  if (sub.startsWith("/glossar/")) return { page: "glossar", slug: decodeURIComponent(sub.slice("/glossar/".length)), base };
  return { page: "notfound", base };
}

const RouterCtx = createContext(null);
function useWissenNav() { return useContext(RouterCtx); }

function useWissenRouter() {
  const [route, setRoute] = useState(() => parseRoute(window.location.pathname));
  useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const navigate = (path) => {
    window.history.pushState({}, "", path);
    setRoute(parseRoute(path));
    window.scrollTo(0, 0);
  };
  return { route, navigate };
}

// A real <a href> — visible to crawlers/no-JS clients, intercepted for SPA nav.
function WLink({ to, children, style, className }) {
  const { navigate } = useWissenNav();
  return (
    <a
      href={to}
      className={className}
      style={style}
      onClick={(e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

// ---------------------------------------------------------------- Styles ---
// Header/footer/root chrome now comes from PublicPageShell/publicHeaderFooterStyles.js
// (shared with the rest of the public site) — only Geo-Wissen's own content styles remain here.
const css = `
  .gw-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 22px 22px; display: block; text-decoration: none; color: inherit; transition: border-color 0.15s, box-shadow 0.15s; }
  .gw-card:hover { border-color: #94a3b8; box-shadow: 0 4px 16px rgba(15,23,42,0.06); }
  .gw-chip { font-family: 'DM Sans', sans-serif; padding: 6px 16px; border-radius: 999px; font-size: 13px; font-weight: 600; cursor: pointer; border: 1.5px solid #e2e8f0; background: transparent; color: #334155; transition: border-color 0.15s, background 0.15s, color 0.15s; }
  .gw-chip:hover { border-color: #94a3b8; }
  .gw-chip.active { border-color: #2563eb; background: #eff6ff; color: #2563eb; }
  .gw-search { width: 100%; max-width: 420px; display: block; margin: 0 auto 24px; padding: 12px 16px; border-radius: 9px; border: 1.5px solid #e2e8f0; font-size: 14px; font-family: 'DM Sans', sans-serif; outline: none; transition: border-color 0.15s; }
  .gw-search:focus { border-color: #2563eb; }
  .gw-badge { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 999px; text-transform: uppercase; letter-spacing: 0.04em; flex-shrink: 0; }
  .gw-badge.neutral { background: #f1f5f9; color: #475569; }
  .gw-badge.accent { background: #dbeafe; color: #2563eb; }
  .gw-related-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px 20px; border: 1px solid #e2e8f0; border-radius: 12px; text-decoration: none; color: inherit; background: #ffffff; transition: border-color 0.15s; }
  .gw-related-row:hover { border-color: #94a3b8; }
  @media (max-width: 720px) {
    .gw-glossar-grid { grid-template-columns: 1fr !important; }
  }
`;

// Header/footer now come from PublicPageShell (PublicHeader/PublicFooter) —
// shared with the rest of the public site.

// ----------------------------------------------------------- Breadcrumb ----
function Breadcrumb({ trail }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#94a3b8", marginBottom: 28, flexWrap: "wrap" }}>
      {trail.map((item, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span>/</span>}
          {item.to ? <WLink to={item.to} style={{ color: "#2563eb", textDecoration: "none" }}>{item.label}</WLink> : <span style={{ color: "#64748b" }}>{item.label}</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

function RelatedTermRow({ slug }) {
  const { lang } = useLanguage();
  const term = getTermBySlug(slug);
  if (!term) return null;
  return (
    <WLink to={`/geo-wissen/glossar/${term.slug}`} className="gw-related-row">
      <div>
        <div style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: 15, color: "#0f172a", marginBottom: 3 }}>{L(term.term, lang)}</div>
        <div style={{ fontSize: 12.5, color: "#64748b", lineHeight: 1.5 }}>{L(term.short, lang)}</div>
      </div>
      <div style={{ fontSize: 15, color: "#2563eb", flexShrink: 0 }}>→</div>
    </WLink>
  );
}

function AuditCta() {
  const { t } = useLanguage();
  return (
    <div style={{ background: "#eff6ff", border: "1px solid #dbeafe", borderRadius: 12, padding: "24px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
      <div>
        <div style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: 17, color: "#0f172a", marginBottom: 4 }}>{t("geoWissen.auditCtaTitle")}</div>
        <div style={{ fontSize: 13, color: "#64748b" }}>{t("geoWissen.auditCtaDesc")}</div>
      </div>
      <a href="/" style={{ padding: "11px 22px", borderRadius: 8, background: "#2563eb", color: "white", fontSize: 14, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap" }}>{t("geoWissen.auditCtaBtn")}</a>
    </div>
  );
}

// -------------------------------------------------------------- Hub page ---
function WissenHubPage() {
  // Aliased to `tr` in this component only: the glossary-term loop variable
  // below is conventionally named `t` (matching term.term/term.full/...
  // field access), which would otherwise shadow the translate function.
  const { t: tr, lang } = useLanguage();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("alle");

  useEffect(() => { document.title = tr("geoWissen.pageTitleHub"); }, [lang]);

  const q = query.trim().toLowerCase();
  const matches = (...fields) => !q || fields.some((f) => f.toLowerCase().includes(q));

  const filteredGlossar = WISSEN_TERMS
    .filter((t) => (category === "alle" || t.category === category) && matches(L(t.term, lang), L(t.full, lang), L(t.short, lang)))
    .sort((a, b) => L(a.term, lang).localeCompare(L(b.term, lang), lang));

  const CATS = ["alle", "grundlagen", "technisch", "produkt"];
  const noResults = filteredGlossar.length === 0;

  return (
    <main style={{ maxWidth: 1040, margin: "0 auto", padding: "56px 24px 96px", width: "100%" }}>
      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600, color: "#2563eb", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 20, padding: "5px 14px", background: "rgba(37,99,235,0.07)", borderRadius: 100, border: "1px solid rgba(37,99,235,0.15)" }}>
          <span style={{ width: 5, height: 5, background: "#2563eb", borderRadius: "50%" }} />
          {tr("geoWissen.eyebrow")}
        </div>
        <h1 style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: "clamp(32px,5vw,48px)", color: "#0f172a", margin: "0 0 16px", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
          {tr("geoWissen.heroTitle")} <em style={{ color: "#2563eb", fontStyle: "italic" }}>{tr("geoWissen.heroTitleEm")}</em>
        </h1>
        <p style={{ fontSize: 16, color: "#64748b", margin: "0 auto", maxWidth: 560, lineHeight: 1.6 }}>
          {tr("geoWissen.heroSubtitle")}
        </p>
      </div>

      <input className="gw-search" placeholder={tr("geoWissen.searchPlaceholder")} value={query} onChange={(e) => setQuery(e.target.value)} />

      <div style={{ textAlign: "center", fontSize: 13, color: "#64748b", marginBottom: 24 }}>
        {tr("geoWissen.glossaryLabel")} <span style={{ fontWeight: 600, color: "#0f172a" }}>({WISSEN_TERMS.length})</span>
      </div>

      <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 32 }}>
        {CATS.map((c) => (
          <button key={c} className={`gw-chip${category === c ? " active" : ""}`} onClick={() => setCategory(c)}>
            {c === "alle" ? tr("geoWissen.catAll") : categoryLabel(c, tr)}
          </button>
        ))}
      </div>

      {noResults ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#64748b", fontSize: 14 }}>{tr("geoWissen.noResults")}</div>
      ) : (
        <div className="gw-glossar-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 16 }}>
          {filteredGlossar.map((t) => (
            <WLink key={t.slug} to={`/geo-wissen/glossar/${t.slug}`} className="gw-card">
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
                <div>
                  <div style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: 20, color: "#0f172a", lineHeight: 1.15 }}>{L(t.term, lang)}</div>
                  <div style={{ fontSize: 12.5, color: "#94a3b8", marginTop: 2 }}>{L(t.full, lang)}</div>
                </div>
                <span className={`gw-badge ${CATEGORY_BADGE_TONE[t.category]}`}>{categoryLabel(t.category, tr)}</span>
              </div>
              <p style={{ fontSize: 13.5, color: "#64748b", lineHeight: 1.6, margin: "8px 0 16px" }}>{L(t.short, lang)}</p>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#2563eb" }}>{tr("geoWissen.readMore")}</div>
            </WLink>
          ))}
        </div>
      )}
    </main>
  );
}

// ------------------------------------------------------- Glossar-Detail ---
function GlossarDetailPage({ slug }) {
  const { t, lang } = useLanguage();
  const term = getTermBySlug(slug);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = term ? `${L(term.term, lang)} ${t("geoWissen.pageTitleTermSuffix")}` : `${t("geoWissen.termNotFoundTitle")} | GeoRev.ai`;
  }, [slug, term, lang]);

  if (!term) {
    return (
      <main style={{ maxWidth: 640, margin: "0 auto", padding: "96px 24px", textAlign: "center" }}>
        <h1 style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: 28 }}>{t("geoWissen.termNotFoundTitle")}.</h1>
        <p style={{ color: "#64748b", marginBottom: 24 }}>{t("geoWissen.termNotFoundDesc")}</p>
        <WLink to="/geo-wissen" style={{ color: "#2563eb", fontWeight: 600, textDecoration: "none" }}>{t("geoWissen.backToGeoWissen")}</WLink>
      </main>
    );
  }

  const termSchema = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    "name": L(term.term, lang),
    "alternateName": L(term.full, lang),
    "description": L(term.definition, lang),
    "inDefinedTermSet": `${SITE_URL}/geo-wissen`,
    "url": `${SITE_URL}/geo-wissen/glossar/${term.slug}`,
  };

  return (
    <main style={{ maxWidth: 720, margin: "0 auto", padding: "48px 24px 96px" }}>
      <JsonLd data={termSchema} />
      <Breadcrumb trail={[{ label: t("geoWissen.eyebrow"), to: "/geo-wissen" }, { label: t("geoWissen.glossaryLabel"), to: "/geo-wissen" }, { label: L(term.term, lang) }]} />
      <span className={`gw-badge ${CATEGORY_BADGE_TONE[term.category]}`}>{categoryLabel(term.category, t)}</span>
      <h1 style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: "clamp(32px,4.5vw,48px)", color: "#0f172a", letterSpacing: "-0.02em", lineHeight: 1.1, margin: "16px 0 6px" }}>{L(term.term, lang)}</h1>
      <div style={{ fontSize: 16, color: "#64748b", marginBottom: 28 }}>{L(term.full, lang)}</div>
      <p style={{ fontSize: 20, color: "#0f172a", lineHeight: 1.5, fontWeight: 500, margin: "0 0 40px" }}>{L(term.definition, lang)}</p>
      {term.context.map((p, i) => <p key={i} style={{ fontSize: 15, color: "#334155", lineHeight: 1.7, margin: "0 0 20px" }}>{L(p, lang)}</p>)}
      {term.bullets && (
        <div style={{ marginBottom: 40 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", marginBottom: 12 }}>{L(term.bulletsIntro, lang)}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {term.bullets.map((b, i) => (
              <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
                <span style={{ fontSize: 13, color: "#2563eb", flexShrink: 0 }}>◆</span>
                <span style={{ fontSize: 14, color: "#334155", lineHeight: 1.5 }}>{L(b, lang)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 40, marginBottom: 40 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 16 }}>{t("geoWissen.relatedTerms")}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>{term.related.map((s) => <RelatedTermRow key={s} slug={s} />)}</div>
      </div>
      <AuditCta />
    </main>
  );
}

function NotFoundPage() {
  const { t, lang } = useLanguage();
  useEffect(() => { document.title = `${t("geoWissen.pageNotFoundTitle")} | GeoRev.ai`; }, [lang]);
  return (
    <main style={{ maxWidth: 560, margin: "0 auto", padding: "120px 24px", textAlign: "center" }}>
      <h1 style={{ fontFamily: "'new-science','DM Sans',sans-serif", fontSize: 30, color: "#0f172a", margin: "0 0 14px" }}>{t("geoWissen.pageNotFoundTitle")}.</h1>
      <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.6, margin: "0 0 28px" }}>{t("geoWissen.pageNotFoundDesc")}</p>
      <WLink to="/geo-wissen" style={{ color: "#2563eb", fontWeight: 600, textDecoration: "none" }}>{t("geoWissen.toGeoWissen")}</WLink>
    </main>
  );
}

// --------------------------------------------------------------- App -------
export default function GeoWissenApp() {
  const { route, navigate } = useWissenRouter();

  let body;
  if (route.page === "hub") body = <WissenHubPage />;
  else if (route.page === "glossar") body = <GlossarDetailPage slug={route.slug} />;
  else body = <NotFoundPage />;

  return (
    <RouterCtx.Provider value={{ route, navigate }}>
      <style>{css}</style>
      {body}
    </RouterCtx.Provider>
  );
}
